import { DatePicker, Select } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { getSemesterMonths, getSemesters } from "../../api/semesters.api";
import { getDtttActivitySummary, getDtttStudents, getDtttSummary } from "../../api/statistics.api";
import Button from "../../components/Button";
import ErrorResponse from "../../components/ErrorResponse";
import SimpleHeader from "../../components/layout/SimpleHeader";
import Loader from "../../components/Loader";
import SelectCampus from "../../components/SelectCampus";
import List from "../../components/statistics/dttt/List";
import Summary from "../../components/statistics/dttt/Summary";
import SelectLevel from "../../components/statistics/e-learning/SelectLevel";
import TabItem from "../../components/statistics/TabItem";
import useAxios from "../../hooks/useAxios";
import useNotification from "../../hooks/useNotification";
import config from "../../lib/config";
import { IGetSemesterMonths, IGetSemesters } from "../../models/semesters.model";
import { IGetDtttActivitySummary, IGetDtttStudents, IGetDtttSummary } from "../../models/statistics.model";
import { RootState } from "../../store/rootReducer";
import EduStepSelect from "../../components/common/EduStepSelect";
import Activity from "../../components/statistics/dttt/Activity";
import TableEmpty from "../../components/common/TableEmpty";

enum Contents {
  Summary,
  Activity,
  List,
}

const StatisticsDttt = () => {
  const { t } = useTranslation();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesters>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetDtttSummary>();
  const { data: res2Month, execute: execute2M } = useAxios<IGetSemesterMonths>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetDtttStudents>();
  const { data: res4, isLoading: isLoading4, error: error4, execute: execute4 } = useAxios<IGetDtttActivitySummary>();

  const [content, setContent] = useState(Contents.Summary);
  const [semesterCode, setSemesterCode] = useState<number>();
  const [eduStepCode, setEduStepCode] = useState<string>();
  const [courseCode, setCourseCode] = useState<string>();
  const [pondTypeCode, setPondTypeCode] = useState<string>();
  const [isFirstRun, setIsFirstRun] = useState(true);
  const [pageNo, setPageNo] = useState(1);
  const pondDefaultTypeCode = 'OT,TS';  //Output, Test

  useEffect(() => {
    execute(getSemesters());

    defSelectBoxSetting();
  }, []);

  const defSelectBoxSetting = () => {
    //검색 필드 Default값 셋팅
    const defSelValue:any = "";
    setEduStepCode(defSelValue);
    setCourseCode(defSelValue);
    setPondTypeCode(pondDefaultTypeCode);
  }

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    if (content === Contents.List && !courseCode) {
      setCourseCode(undefined);
    }

    getDtttData(1);
  }, [content]);

  useEffect(() => {
    if (isFirstRun) {
      return;
    }

    getDtttData();
  }, [pageNo]);

  const getDtttData = (_pageNo?: number) => {
    if (!semesterCode) {
      return infoNotification(t('statistics.dttt.message1'));
    }
    if (content === Contents.List && !courseCode) {
      return infoNotification(t('statistics.dttt.message3'));
    }

    const req: any = {
      semesterCode,
      campusCode: currentCampus,
      courseCode,
    };
    if (eduStepCode) {
      req.eduStepCode = eduStepCode;
    }
    
    if (pondTypeCode) {
      req.pondTypeCode = pondTypeCode;
    } else {
      req.pondTypeCode = pondDefaultTypeCode;
      setPondTypeCode(pondDefaultTypeCode);
    }

    switch (content) {
      case Contents.Summary:
        if (semesterCode) {
          execute2M(getSemesterMonths(String(semesterCode)));
        }
        execute2(getDtttSummary(req));
        break;
      case Contents.List:
        req.perPage = config.pageSize;
        req.pageNo = _pageNo || pageNo;
        execute3(getDtttStudents(req));
        break;
      case Contents.Activity:
        execute4(getDtttActivitySummary(req));
        break;
    }
  }

  const renderContent = () => {
    switch (content) {
      case Contents.Summary:
        return (
          <Summary
            rawData={res2?.data}
            rawData2={res2Month?.data}
          />
        );
      case Contents.List:
        return (
          <List
            res={res3}
            pageNo={pageNo}
            setPageNo={setPageNo}
            semesterCode={semesterCode}
            courseCode={courseCode}
            pondTypeCode={pondTypeCode}
          />
        );
      case Contents.Activity:
        return (
          <Activity res={res4?.data}></Activity>
        )
    }
  }

  return (
    <>
    <SimpleHeader
        iconFile="icon-statistics-big.svg"
        parentMenu={t('common.menu.statistics')}
        currentMenu={t('statistics.dttt.title1')}
        onlyTitle={true}
    />
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label> {/* 캠퍼스 */}
            <div><SelectCampus /></div>
          </li>
          <li>
            <label>{t('class.class.placeholder4')}</label>  {/* 학기 */}
            <Select
              placeholder={t('statistics.regularTest.placeholder2')}
              value={semesterCode}
              onChange={value => setSemesterCode(value)}
            >
              {res?.data?.filter(item => item.semesterType === 'RG').map(item => (  
                <Select.Option
                  key={item.semesterCode}
                  value={item.semesterCode}
                >
                  {item.semesterName}
                </Select.Option>
              ))}
            </Select>
          </li>
          <li>
            <label>{t('common.placeholder.1')}</label> {/* 교육단계 */}
            <EduStepSelect
              eduStepCode={eduStepCode}
              setEduStepCode={value => {
                setEduStepCode(value);

                if (content === Contents.List) {
                  setCourseCode(undefined);
                } else {
                  const defSelValue:any = "";
                  setCourseCode(defSelValue);
                }
              }}
              width={90}
              includeAll
            />
          </li>
          <li>
            <label>{t('student.student.column26')}</label> {/* 레벨 */} 
            <div>  
              <SelectLevel
                courseCode={courseCode}
                setCourseCode={setCourseCode}
                includeAll={content === Contents.Summary || content === Contents.Activity}
                eduStepCode={eduStepCode}
                placeholder={t('statistics.regularTest.placeholder2')}
              />
            </div>
          </li>
          <li>
            <label>{t('statistics.regularTest.placeholder1')}</label> {/* 유형 */}
            <Select
              placeholder={t('statistics.eLearning.column1')}
              value={pondTypeCode}
              onChange={value => setPondTypeCode(value)}
            >
                <Select.Option value={pondDefaultTypeCode}>
                  {t('common.text.all')}
                </Select.Option>
                <Select.Option value="OT">
                  {'Output'}
                </Select.Option>
                <Select.Option value="TS">
                  {'Test'}
                </Select.Option>
            </Select>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={() => getDtttData(1)}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {(isLoading || isLoading2 || isLoading3 || isLoading4) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={error4} />
      {
        res2?.data || res3?.data || res4?.data
          ? (
            <>
              <div className="comm-tab__line">
                <ul>
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Summary}
                    tabLabel={t('statistics.attendance.Tab1')}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.Activity}
                    tabLabel={'Activity'}
                  />
                  <TabItem
                    currentContent={content}
                    onSetContent={setContent}
                    tabContent={Contents.List}
                    tabLabel={t('statistics.homework.tab1')}
                  />
                </ul>
              </div>
              <div>
                {renderContent()}
              </div>
            </>
            )
          : isLoading2 || isLoading3 || isLoading4
            ? null
            : (
              <TableEmpty text={t('statistics.appday.text1')} />
              )
      }        
    </div>
    </>
  );
}

export default StatisticsDttt;
